/**
 * CL(2026-10-15 §1-6 · §4-4) — 전역 푸터 = «정적 허브 링크». 내부 링크 손실을 막는 자리다.
 *   ① 시도 허브(regions 표 tier=1 정본 slug — layout 이 서버에서 넘긴다, 비면 고정 목록)
 *   ② 도구(계산기·가점 진단·부동산 통계)
 *   ③ 분석 글(부동산 분석 글 · 분양 · 재개발·재건축 · 청약 일정)
 *   ④ 회사(소개·약관·개인정보·보도자료)
 *
 * ⛔ 훅 없음 · 방문자 무관(같은 URL = 같은 HTML). 서버·클라이언트 어디서나 렌더 가능.
 * ⛔ 카톡방·카페·Hostinger 위성 링크 없음. 전화번호 없음. noindex·301 로 접힌 라우트(/feed·/discuss·/stock 하위)를 넣지 않는다.
 */
import { CONTACT_EMAIL } from '@/lib/constants';
import { TOOLS_HREF } from '@/lib/nav/nav-config';
import { sidoDisplayLabel } from '@/lib/ds2/sido-label';
import type { CompanyInfo } from '@/lib/company-info-shared';
import CompanyInfoLine from './CompanyInfoLine';

export interface FooterTop { code: string; label: string; href: string }

/** regions 표 조회 실패 시 폴백 — 2026-10-15 regions tier=1 정본 slug 16개(전남광주 통합). */
export const FOOTER_SIDO_FALLBACK: readonly string[] = [
  '서울', '부산', '대구', '인천', '전남광주', '대전', '울산', '세종', '경기', '강원', '충북', '충남', '전북', '경북', '경남', '제주',
];

const sidoHref = (slug: string) => `/apt/region/${encodeURIComponent(slug)}`;

export const FOOTER_TOOLS: readonly { label: string; href: string }[] = [
  { label: '계산기', href: TOOLS_HREF },
  { label: '가점 진단', href: '/apt/diagnose' },
  { label: '부동산 통계', href: '/apt/data' },
];

export const FOOTER_DOCS: readonly { label: string; href: string }[] = [
  { label: '분양', href: '/apt' },
  { label: '청약 일정', href: '/calendar' },
  { label: '재개발·재건축', href: '/apt/redev' },
  { label: '지역별 분양', href: '/apt/region' },
  { label: '분석 글', href: '/blog' },
];

/** DS2(ds2/sian/sian_chrome) — 1줄 링크(시안 순서). 「지역별 분양」 은 아래 시도 줄이 대신한다. */
/* google1025 ② (GSC URL 검사 10/25: /apt/region·/apt/busan·/apt/unsold 「Google 에 알려지지 않은 URL」) — 색인 허브를 서버 HTML 전역 푸터에 «평범한 <a>» 로 잇는다.
 *   상단 메뉴(Navigation)는 ssr:false 라 서버 HTML 에 링크가 없다. ⛔ /apt/popular 는 넣지 않는다 — 그 페이지는 robots noindex(s8 죽은 라우트)다. */
export const FOOTER_HUBS: readonly { label: string; href: string }[] = [
  { label: '지역별 분양', href: '/apt/region' },
  // KWX v2 레인 C — /apt/busan 은 시도 허브로 301 통합(next.config) · 링크도 정본으로(3xx 를 잇지 않는다).
  { label: '부산 아파트 분양', href: '/apt/region/%EB%B6%80%EC%82%B0' },
  { label: '미분양', href: '/apt/unsold' },
];
const DS2_DOCS: readonly { label: string; href: string }[] = [
  ...FOOTER_DOCS.filter((l) => l.href !== '/apt/region'),
  ...FOOTER_HUBS,
];
const DS2_COMPANY: readonly { label: string; href: string; strong?: boolean }[] = [
  { label: '이용약관', href: '/terms' },
  { label: '개인정보처리방침', href: '/privacy', strong: true },
  { label: '광고 안내', href: '/advertise' },
];

/**
 * DS2 — 2줄 컴팩트 푸터(sian_chrome): 1줄 = 로고 + 분양·청약 일정·재개발·재건축·분석 글·도구·이용약관·개인정보처리방침·문의,
 * 2줄 = 출처 문구. CL §1-6 의 시도 허브 링크(내부 링크 보존)는 1줄과 2줄 사이 12px 한 줄로 유지한다(ledger).
 * 전화번호·© 연도 줄 없음(시안 — 연도 리터럴 재유입 0).
 * ADS-F(2026-10-24) — 2줄 아래 «사업자 정보» 접기 한 줄(전자상거래법). 값은 app_config 'company'(layout 이 서버에서 읽어 내려준다).
 *   빈 항목은 숨기고 전부 비면 아무것도 안 그린다(지금 모습 유지).
 */
export default function SiteFooter({ tops, company }: { tops?: readonly FooterTop[] | null; company?: CompanyInfo | null }) {
  const sido = tops && tops.length
    ? tops.map((t) => ({ key: t.code, label: t.label, href: t.href }))
    : FOOTER_SIDO_FALLBACK.map((s) => ({ key: s, label: s, href: sidoHref(s) }));
  return (
    <footer className="kd-site-footer kd-ft" data-cl-footer="">
      <div className="kd-ft__wrap">
        <div className="kd-ft__r1">
          <a href="/" className="kd-ft__logo">카더라</a>
          <nav aria-label="분양 정보" data-footer-row="docs" className="kd-ft__grp">
            {DS2_DOCS.map((l) => <a key={l.href} href={l.href}>{l.label}</a>)}
          </nav>
          <nav aria-label="도구" data-footer-row="tools" className="kd-ft__grp">
            <a href={FOOTER_TOOLS[0].href}>도구</a>
          </nav>
          <nav aria-label="회사" data-footer-row="company" className="kd-ft__grp">
            {DS2_COMPANY.map((l) => <a key={l.href} href={l.href}>{l.strong ? <b>{l.label}</b> : l.label}</a>)}
            <a href={`mailto:${CONTACT_EMAIL}`}>문의</a>
          </nav>
        </div>
        <nav aria-label="지역별 분양" data-footer-row="sido" className="kd-ft__sido">
          {sido.map((s) => <a key={s.key} href={s.href}>{sidoDisplayLabel(s.label)}</a>)}
        </nav>
        {/* ai-cite (2026-10-26) — 출처 줄에서 편집 원칙·데이터 출처(/about/data)로 잇는다(서버 HTML 평범한 <a>). */}
        <p className="kd-ft__r2">출처: 청약홈 입주자모집공고 · 국토교통부 실거래가 · 지자체 정비사업 공개자료. 공식 공고와 다를 수 있으니 청약 전 원문을 확인하세요. <a href="/about/data">편집 원칙·데이터 출처</a></p>
        <CompanyInfoLine info={company} />
      </div>
    </footer>
  );
}
